import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionHeading } from './_shared';
import { motion, useReducedMotion } from 'framer-motion';
import { fetchPublicProjects, type PublicProject } from '../../../services/projectService';
import { formatAmount } from '../../../utils/numberFormat';

// "Featured new projects" — real off-plan / new developments from /api/projects.
// No fabricated "from" price (PublicProject has no per-unit price); we show the
// build status, city, unit count and a verified-developer mark instead.
export default function FeaturedProjects() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';

  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [loaded, setLoaded] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    let cancelled = false;
    fetchPublicProjects({ pageSize: 6 })
      .then((res) => {
        if (!cancelled) setProjects(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loaded && projects.length === 0) return null;

  return (
    <section className="bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          title={t('home.v2.projects.title')}
          action={{ label: t('home.v2.projects.all'), to: '/investors/licensed-projects' }}
          isRTL={isRTL}
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(loaded ? projects : Array.from({ length: 3 })).map((raw, i) => {
            if (!raw) {
              return <div key={i} className="h-72 animate-pulse rounded-2xl border border-[var(--pv-border-soft)] bg-white" />;
            }
            const p = raw as PublicProject;
            const isReady = p.status === 'READY' || p.status === 'SOLD_OUT';
            const name = isRTL ? p.nameAr || p.name : p.name;
            return (
              <motion.article
                key={p.id}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-[0_10px_30px_-18px_rgba(0,77,64,0.25)] transition hover:-translate-y-1 hover:shadow-[0_26px_50px_-24px_rgba(0,77,64,0.4)]"
                initial={reduce ? false : { opacity: 0, y: 20 }}
                whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/developers/project/${p.slug}`}
                  aria-label={name}
                  className="absolute inset-0 z-20 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--pv-color-primary)]"
                />
                <div className="relative h-44 w-full overflow-hidden bg-purple-50">
                  {p.coverUrl ? (
                    <img
                      src={p.coverUrl}
                      alt=""
                      width={400}
                      height={176}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#004D40]">
                      <i className="ri-building-4-line text-5xl text-white/50" aria-hidden="true" />
                    </div>
                  )}
                  <span
                    className={`absolute top-3 rounded-full bg-[#FEF8E8] px-2.5 py-1 text-[11px] font-semibold text-[#8C6A0B] ring-1 ring-[#FBD35A] shadow-sm ${
                      isRTL ? 'right-3' : 'left-3'
                    }`}
                  >
                    {isReady ? t('home.v2.projects.ready') : t('home.v2.projects.offplan')}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 sm:p-5">
                  <h3 className="flex items-center gap-1.5 font-display text-lg font-semibold text-ink">
                    <span className="line-clamp-1 min-w-0">{name}</span>
                    {p.developer?.isVerified && (
                      <i className="ri-verified-badge-fill shrink-0 text-sm text-mq-primary" aria-hidden="true" />
                    )}
                  </h3>
                  {p.city && (
                    <p className="mt-1 flex items-center gap-1 text-sm text-ink-muted">
                      <i className="ri-map-pin-2-line text-xs" aria-hidden="true" />
                      {p.city}
                    </p>
                  )}
                  <div className="mt-4 flex items-center justify-between border-t border-[var(--pv-border-soft)] pt-3 text-sm text-ink-muted">
                    {typeof p.totalUnits === 'number' && p.totalUnits > 0 ? (
                      <span className="tabular-nums">{t('home.v2.projects.units', { n: formatAmount(p.totalUnits) })}</span>
                    ) : (
                      <span />
                    )}
                    {p.developer?.name && (
                      <span className="line-clamp-1 font-medium text-mq-primary">
                        {isRTL ? p.developer.nameAr || p.developer.name : p.developer.name}
                      </span>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
